import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import { LocationFeatureDto } from '@flyfreely-portal-ui/flyfreely';
import { Polygon } from 'geojson';
import { circleFeatureTypes } from '../constants';
import { FeatureAndGroup, FeatureGroup, MapFeature } from '../interfaces';
import { bufferPolygon } from '../utils';
@Component({
    selector: 'map-marker-toolbar',
    templateUrl: './map-marker-toolbar.component.html',
    styleUrls: ['./map-marker-toolbar.component.scss']
})
export class MapMarkerToolbarComponent implements OnChanges, OnDestroy {
    @Input()
    public feature: MapFeature;

    @Input()
    public featureGroup: FeatureGroup;

    @Input()
    public allowEditCoordinates = false;

    @Input()
    public canConvertToCorridor = false;

    @Input()
    public canBufferPolygon = false;

    @Input()
    public canCancelConversion = false;

    @Output()
    public onFeatureChange: EventEmitter<FeatureAndGroup>;

    @Output()
    public onFeatureEdit: EventEmitter<FeatureAndGroup>;

    @Output()
    public onFeatureDelete: EventEmitter<FeatureAndGroup>;

    @Output()
    public onFeatureConvert: EventEmitter<FeatureAndGroup>;

    @Output()
    public onConvertCancel: EventEmitter<FeatureAndGroup>;

    /** Emitted when the user picks a different category in the radio group,
     * in addition to onFeatureChange. */
    @Output()
    public categoryChange: EventEmitter<FeatureAndGroup>;

    public popupView: 'default' | 'buffer' = 'default';

    public bufferMeters = 0;

    /** Geometry snapshot taken when the popup selects this feature, so that
     * repeated ngModelChange keystrokes buffer from a fixed shape instead of
     * accumulating on top of the previous keystroke's result. */
    private bufferBaseline: Polygon | null = null;

    constructor() {
        this.onFeatureEdit = new EventEmitter<FeatureAndGroup>();
        this.onFeatureDelete = new EventEmitter<FeatureAndGroup>();
        this.onFeatureChange = new EventEmitter<FeatureAndGroup>();
        this.onFeatureConvert = new EventEmitter<FeatureAndGroup>();
        this.onConvertCancel = new EventEmitter<FeatureAndGroup>();
        this.categoryChange = new EventEmitter<FeatureAndGroup>();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['feature']) {
            const previous = changes['feature'].previousValue as MapFeature;
            // Ordering constraint: this block must run before the resets
            // below, since it consumes the previous session's state.
            if (
                previous != null &&
                this.popupView === 'buffer' &&
                this.bufferMeters !== 0 &&
                this.bufferBaseline != null
            ) {
                previous.geom = this.bufferBaseline;
                this.onFeatureChange.emit({
                    feature: previous,
                    groupId: (
                        changes['featureGroup']?.previousValue ??
                        this.featureGroup
                    )?.id
                });
            }
            this.popupView = 'default';
            this.bufferMeters = 0;
            this.bufferBaseline =
                this.feature?.geom?.type === 'Polygon'
                    ? this.feature.geom
                    : null;
        }
    }

    ngOnDestroy() {
        this.revertUnappliedBuffer();
    }

    public revertUnappliedBuffer() {
        if (this.popupView !== 'buffer') {
            return;
        }
        if (this.bufferMeters !== 0 && this.bufferBaseline != null) {
            this.feature.geom = this.bufferBaseline;
            this.onFeatureChange.emit(this.getFeatureAndGroup());
        }
        this.popupView = 'default';
        this.bufferMeters = 0;
    }

    public onFeatureNameChange() {
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    public onFeatureCoordinatesChange() {
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    public onFeatureOffsetChange() {
        if (this.feature.properties.lineOffsetMeters < 1) {
            this.feature.properties.lineOffsetMeters = 1;
        }
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    public onFeatureRadiusChange() {
        if (this.feature.properties.radiusMetres < 1) {
            this.feature.properties.radiusMetres = 1;
        }
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    get isCircleFeature(): boolean {
        // A feature still being drawn has no properties yet
        return (
            this.feature?.properties != null &&
            circleFeatureTypes.includes(
                <LocationFeatureDto.Type>this.feature.categoryId
            )
        );
    }

    public onFeatureEditClick() {
        this.onFeatureEdit.emit(this.getFeatureAndGroup());
    }

    public onFeatureDeleteClick() {
        this.onFeatureDelete.emit(this.getFeatureAndGroup());
    }

    public onCategoryChange() {
        this.categoryChange.emit(this.getFeatureAndGroup());
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    public openBufferView() {
        this.popupView = 'buffer';
        this.bufferMeters = 0;
        this.bufferBaseline =
            this.feature?.geom?.type === 'Polygon' ? this.feature.geom : null;
    }

    public cancelBufferView() {
        this.revertUnappliedBuffer();
    }

    public applyBufferView() {
        // Geometry has already been written by the live preview in
        // onBufferChange; applying commits to it by closing the dialogue and
        // emitting once more, after the reset, so that consumers tracking
        // history can tell the commit from the previews.
        this.popupView = 'default';
        this.bufferMeters = 0;
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    public onConvertCancelClick() {
        this.onConvertCancel.emit(this.getFeatureAndGroup());
    }

    public onBufferChange() {
        if (this.bufferBaseline == null) {
            return;
        }
        if (this.bufferMeters == null) {
            this.bufferMeters = 0;
        }
        if (this.bufferMeters === 0) {
            this.feature.geom = this.bufferBaseline;
        } else {
            // A negative distance shrinks the polygon. bufferPolygon returns
            // null once shrinking would destroy the shape, leaving the preview
            // frozen at the last valid one.
            const buffered = bufferPolygon(
                this.bufferBaseline,
                this.bufferMeters
            );
            if (buffered == null) {
                return;
            }
            this.feature.geom = buffered.geometry;
        }
        this.onFeatureChange.emit(this.getFeatureAndGroup());
    }

    public onConvertClick() {
        this.onFeatureConvert.emit(this.getFeatureAndGroup());
    }

    private getFeatureAndGroup(): FeatureAndGroup {
        return {
            feature: this.feature,
            groupId: this.featureGroup.id
        };
    }
}
